/**
 * ContactOptions — 상담원 연결 안내 카드
 *
 * 환경별 분기:
 * - PC          : 1:1 온라인 상담 + 전화번호 정보 박스 (정보만 노출)
 * - 모바일      : 1:1 온라인 상담 + tel: 링크
 *                 운영시간 외에는 전화 박스를 흐리게 (비활성)
 *
 * 1:1 온라인 상담은 PC·모바일 공통이며 운영시간 무관 항상 활성.
 */

import { cn } from '@/lib/utils';
import { isMobileDevice } from '@/lib/utils/device';

interface ContactOptionsProps {
  isOperating: boolean;
  supportPhone: string;
  // exactOptionalPropertyTypes 환경에서 호출부가 `string | undefined`를 그대로 넘길 수 있도록
  // undefined를 명시적으로 허용한다. 기본값은 destructuring에서 처리한다.
  onlineContactUrl?: string | undefined;
}

export function ContactOptions({
  isOperating,
  supportPhone,
  onlineContactUrl = '/app/counsel.jsp',
}: ContactOptionsProps) {
  const isMobile = isMobileDevice();

  return (
    <div className="ml-flex ml-flex-col ml-gap-2 ml-mt-3">
      {/* 1:1 온라인 상담 — PC·모바일 공통, 항상 표시 */}
      <a
        href={onlineContactUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          'ml-flex ml-items-start ml-gap-3 ml-p-3',
          'ml-bg-brand-50 ml-border ml-border-brand-200',
          'ml-rounded-cb-md ml-no-underline ml-text-ink',
          'hover:ml-bg-brand-100 ml-transition-colors'
        )}
      >
        <span className="ml-text-xl ml-flex-shrink-0">📝</span>
        <div className="ml-flex-1 ml-min-w-0">
          <div className="ml-text-cb-base ml-font-semibold ml-text-brand-600">
            1:1 온라인 상담 남기기
          </div>
          <div className="ml-text-cb-sm ml-text-mute ml-mt-0.5">
            {isOperating
              ? '영업일 기준 1일 이내 답변'
              : '익영업일 오전에 답변 드립니다'}
          </div>
        </div>
      </a>

      {/* 전화 — 환경별 분기 */}
      {isMobile ? (
        isOperating ? (
          // 모바일 + 운영시간 중 → tel: 링크
          <a
            href={`tel:${supportPhone.replace(/-/g, '')}`}
            className={cn(
              'ml-flex ml-items-start ml-gap-3 ml-p-3',
              'ml-bg-ok/10 ml-border ml-border-ok/30',
              'ml-rounded-cb-md ml-no-underline ml-text-ink',
              'hover:ml-bg-ok/20 ml-transition-colors'
            )}
          >
            <span className="ml-text-xl ml-flex-shrink-0">☎</span>
            <div className="ml-flex-1">
              <div className="ml-text-cb-base ml-font-semibold ml-text-ok">
                지금 전화 걸기
              </div>
              <div className="ml-text-cb-sm ml-text-mute ml-mt-0.5">
                {supportPhone} · 운영시간 중
              </div>
            </div>
          </a>
        ) : (
          // 모바일 + 운영시간 외 → 비활성 정보 박스
          <div
            className={cn(
              'ml-flex ml-items-start ml-gap-3 ml-p-3',
              'ml-bg-surface ml-border ml-border-line',
              'ml-rounded-cb-md ml-opacity-60'
            )}
          >
            <span className="ml-text-xl ml-flex-shrink-0">☎</span>
            <div className="ml-flex-1">
              <div className="ml-text-cb-base ml-font-semibold ml-text-mute">
                {supportPhone}
              </div>
              <div className="ml-text-cb-sm ml-text-mute ml-mt-0.5">
                운영시간 외 (평일 09:00~18:00)
              </div>
            </div>
          </div>
        )
      ) : (
        // PC → 정보 제공 전용 박스
        <div
          className={cn(
            'ml-flex ml-items-start ml-gap-3 ml-p-3',
            'ml-bg-surface ml-border ml-border-line ml-rounded-cb-md'
          )}
        >
          <span className="ml-text-xl ml-flex-shrink-0">☎</span>
          <div className="ml-flex-1">
            <div className="ml-text-cb-base ml-font-semibold ml-text-ink">
              고객센터 {supportPhone}
            </div>
            <div className="ml-text-cb-sm ml-text-mute ml-mt-0.5">
              {isOperating
                ? '평일 09:00 ~ 18:00 운영 중'
                : '운영시간 외 (평일 09:00 ~ 18:00)'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
